import { captureException } from "@sentry/react"
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  toast,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import { useMediaQuery } from "@uidotdev/usehooks"
import api from "api"
import { queries } from "api/queries"
import { APIError } from "api/utils"
import clsx from "clsx"
import FullPageLoading from "components/FullPageLoading"
import { useMotionValueEvent, useScroll } from "motion/react"
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { FormProvider, useForm } from "react-hook-form"
import {
  BROWSER_STORAGE_KEYS,
  IS_DEV_ENVIRONMENT,
  IS_LOCALHOST,
} from "utils/constants"
import { breakpoints } from "utils/createBreakpoints"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { isShadow } from "utils/shadow"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import { UserPreferenceManager } from "utils/userPreferenceManager"

import Animation from "./Animation"
import {
  currentStepTitle,
  DSB_AGREEMENT_SLUG,
  OnboardingStepId,
  SIGNATURE_FIELD_NAME,
} from "./constants"
import LearnersAgreement from "./LearnersAgreement"
import PrivacyPolicy from "./PrivacyPolicy"
import TDC_DSBAgreement from "./TDC_DSBAgreement"
import TermsOfUse from "./TermsOfUse"
import {
  getPgCTLProductSlug,
  getPgCTLProgramName,
  getSignatureError,
} from "./utils"

const tempDismissKey = "temp_pgctlEnrollmentAgreementDismissed"

type Form = {
  name: string
  learnerAgreement: boolean
  termsOfUse: boolean
  privacyPolicy: boolean
  tdc_dsbAgreement: boolean
}

const PgCTLEnrollmentAgreement = (props: PropsWithChildren) => {
  const hasDismissedTemporarily =
    sessionStorage.getItem(tempDismissKey) === "true"
  const [animationDirection, setAnimationDirection] = useState(1)
  const [currentStep, setCurrentStep] = useState(0)
  const [sheetBodyRef, setSheetBodyRef] = useState<HTMLDivElement | null>(null)
  const [hasNotScrolled, setHasNotScrolled] = useState(true)
  const [agreementLoaded, setAgreementLoaded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    isLoading,
    pgctl: { deliveryMode, isNewDeliveryMode },
  } = useEnrollments()

  const pgctlEnrollmentAgreementAccepted = !!UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.pgctlEnrollmentAgreementAccepted
  )

  const productSlug = deliveryMode ? getPgCTLProductSlug(deliveryMode) : null

  /**
   * The documents the learner has to sign, and therefore the steps of this
   * flow, come from the backend. That list is batch scoped, which is how the
   * DSB agreement reaches TDC learners.
   */
  const agreementSlugs = useQuery({
    enabled: Boolean(
      productSlug &&
        isNewDeliveryMode &&
        !pgctlEnrollmentAgreementAccepted &&
        !hasDismissedTemporarily
    ),
    queryKey: queries.enrollmentAgreements.slugs(productSlug || "").queryKey,
    queryFn: () =>
      api.enrollmentAgreements.listSlugs({
        urlParams: { productSlug: productSlug! },
      }),
    /**
     * The query client disables retries globally. This component lives in
     * the auth wrapper and never remounts on navigation, so a single
     * transient failure would otherwise leave the learner unasked until a
     * hard reload.
     */
    retry: 3,
  })

  const slugs = agreementSlugs.data?.slugs
  const enableTdcDsbAgreement = Boolean(slugs?.includes(DSB_AGREEMENT_SLUG))

  const steps = [
    { id: OnboardingStepId.learnersAgreement, component: LearnersAgreement },
    { id: OnboardingStepId.termsOfUse, component: TermsOfUse },
    { id: OnboardingStepId.privacyPolicy, component: PrivacyPolicy },
    ...(enableTdcDsbAgreement
      ? [{ id: OnboardingStepId.tdc_dsbAgreement, component: TDC_DSBAgreement }]
      : []),
  ]

  const Component = steps[currentStep]?.component
  const { scrollY } = useScroll({ container: { current: sheetBodyRef } })

  const isXs = useMediaQuery(breakpoints.down("xs"))

  const form = useForm<Form>({
    defaultValues: {
      name: "",
      learnerAgreement: false,
      privacyPolicy: false,
      termsOfUse: false,
      tdc_dsbAgreement: false,
    },
  })

  const [open, setOpen] = useState(false)

  const programName = deliveryMode
    ? getPgCTLProgramName(deliveryMode)
    : { shortName: "", modeDisplay: "" }

  /**
   * Every event in this flow carries the same product context so any one of
   * them can be broken down by programme in PostHog.
   */
  const track = (eventName: string, extra?: object) => {
    trackingService.trackEvent(
      eventName,
      {
        productName: programName.shortName,
        product_name: programName.shortName,
        product_slug: productSlug,
        delivery_mode: deliveryMode,
        slugs,
        total_steps: steps.length,
        ...extra,
      },
      { services: ["PostHog"] }
    )
  }

  /**
   * `step_id`, not `step`. PostHog types a property once per project and
   * `step` is already numeric, so a string value there reads as null.
   */
  const stepData = (stepNo: number) => ({
    step_id: steps[stepNo]?.id,
    step_index: stepNo,
  })

  /**
   * A failed lookup means the learner is never asked to sign, so it must be
   * loud. Logged once the query's retries are exhausted.
   */
  useEffect(() => {
    if (!agreementSlugs.isError) return

    const statusCode =
      agreementSlugs.error instanceof APIError
        ? agreementSlugs.error.statusCode
        : undefined
    const error =
      agreementSlugs.error instanceof Error
        ? agreementSlugs.error.message
        : String(agreementSlugs.error)

    captureException(
      new Error("Failed to fetch enrollment agreement slugs"),
      scope => {
        scope.setExtras({ productSlug, deliveryMode, statusCode, error })
        return scope
      }
    )
    track(TrackingEvents.enrollmentAgreementLoadFailed, {
      status_code: statusCode,
      error,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementSlugs.isError])

  useEffect(() => {
    if (hasDismissedTemporarily || pgctlEnrollmentAgreementAccepted) return
    /**
     * Nothing to show until we know which documents the learner is eligible
     * to sign. A failed lookup leaves the flow closed instead of blocking the
     * learner behind a sheet that cannot be submitted.
     */
    if (!slugs || slugs.length === 0) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isNewDeliveryMode) setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMode, pgctlEnrollmentAgreementAccepted, slugs])

  /**
   * Presented fires once per mount. Step viewed fires for every step,
   * including the first, so the funnel reads presented → viewed(0) →
   * completed(0) → viewed(1) → … → completed.
   */
  const presentedRef = useRef(false)
  useEffect(() => {
    if (!open) return

    if (!presentedRef.current) {
      presentedRef.current = true
      track(TrackingEvents.enrollmentAgreementPresented)
    }
    track(TrackingEvents.enrollmentAgreementStepViewed, stepData(currentStep))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentStep])

  useMotionValueEvent(scrollY, "change", latest => {
    if (latest >= 30) {
      setHasNotScrolled(false)
    } else {
      setHasNotScrolled(true)
    }
  })

  const handleClose = () => {
    track(TrackingEvents.enrollmentAgreementDismissed, stepData(currentStep))
    setOpen(false)
    sessionStorage.setItem(tempDismissKey, "true")
  }

  const toastPosition = {
    position: isXs ? "top-center" : "bottom-center",
  } as const

  /**
   * The signature field and the checkbox sit at the end of the document, so
   * bring them into view whenever they block the learner from moving on.
   */
  const scrollToBottom = () => {
    if (!sheetBodyRef) return

    sheetBodyRef.scrollTo({
      top: sheetBodyRef.scrollHeight,
      behavior: "smooth",
    })
  }

  const validForNextStep = (stepNo: number) => {
    const step = steps[stepNo]

    const signatureError = getSignatureError(
      form.getValues(SIGNATURE_FIELD_NAME)
    )

    if (signatureError) {
      track(TrackingEvents.enrollmentAgreementValidationFailed, {
        ...stepData(stepNo),
        reason: "signature",
      })
      form.setError(SIGNATURE_FIELD_NAME, { message: signatureError })
      toast.warning(
        "Please type your full name to sign this document.",
        toastPosition
      )
      scrollToBottom()
      return false
    }

    const value = form.getValues(step.id)

    if (!value) {
      track(TrackingEvents.enrollmentAgreementValidationFailed, {
        ...stepData(stepNo),
        reason: "checkbox",
      })
      form.setError(step.id, { message: "Required" })
      toast.warning(
        `Please read and accept the ${
          currentStepTitle[step.id]
        } before proceeding.`,
        toastPosition
      )
      scrollToBottom()
      return false
    }
    return true
  }

  const onSubmit = form.handleSubmit(
    async values => {
      const isValid = validForNextStep(currentStep)

      if (!isValid) return

      if (
        !values.learnerAgreement ||
        !values.termsOfUse ||
        !values.privacyPolicy
      ) {
        toast.error("Please agree to all agreements")
        return
      }

      track(
        TrackingEvents.enrollmentAgreementStepCompleted,
        stepData(currentStep)
      )

      if (!productSlug || !slugs || slugs.length === 0) {
        captureException(
          new Error("Enrollment agreement submitted without documents"),
          scope => {
            scope.setExtras({ productSlug, deliveryMode, slugs })
            return scope
          }
        )
        toast.error("Something went wrong. Please refresh and try again.")
        return
      }

      setIsSubmitting(true)

      try {
        await api.enrollmentAgreements.accept({
          data: {
            productSlug,
            fullName: values.name.trim(),
            slugs,
          },
        })
      } catch (error) {
        const statusCode =
          error instanceof APIError ? error.statusCode : undefined
        const errorMessage =
          error instanceof Error ? error.message : String(error)

        captureException(
          new Error("Failed to accept enrollment agreements"),
          scope => {
            scope.setExtras({
              productSlug,
              slugs,
              statusCode,
              error: errorMessage,
            })
            return scope
          }
        )
        track(TrackingEvents.enrollmentAgreementSubmitFailed, {
          status_code: statusCode,
          error: errorMessage,
        })

        const message =
          error instanceof APIError && error.errors.message
            ? error.errors.message
            : "We couldn't record your signature. Please try again."
        toast.error(message)
        return
      } finally {
        setIsSubmitting(false)
      }

      toast.success(
        `You've agreed to the ${programName.shortName} Enrollment Agreement`
      )
      setOpen(false)

      const acceptedAt = new Date().toISOString()

      track(TrackingEvents.enrollmentAgreementCompleted, {
        ...stepData(currentStep),
        signed_at: acceptedAt,
      })
      trackingService.setPostHogUserAttribute({
        pgctl_enrollment_agreement_signed: true,
        pgctl_enrollment_agreement_signed_at: acceptedAt,
        pgctl_enrollment_agreement_product_slug: productSlug,
      })
      UserPreferenceManager.set(
        BROWSER_STORAGE_KEYS.pgctlEnrollmentAgreementAccepted,
        acceptedAt
      )
    },
    () => validForNextStep(currentStep)
  )

  const goToNextPage = useCallback(() => {
    const isValid = validForNextStep(currentStep)

    if (!isValid) return

    track(
      TrackingEvents.enrollmentAgreementStepCompleted,
      stepData(currentStep)
    )

    if (sheetBodyRef) {
      sheetBodyRef.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }

    setAnimationDirection(1)
    setCurrentStep(prev => prev + 1)
    setAgreementLoaded(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, sheetBodyRef])

  if (isLoading)
    return (
      <FullPageLoading debugMessage="From PgCTLEnrollmentAgreement/index.tsx" />
    )

  if (!open) return props.children

  const allowDismiss = isShadow() || IS_LOCALHOST || IS_DEV_ENVIRONMENT

  return (
    <Sheet open={open} onOpenChange={allowDismiss ? handleClose : undefined}>
      <SheetContent height={95} className="p-0">
        <SheetHeader
          hideClose={!allowDismiss}
          className={clsx("z-1 items-center pb-1.25", {
            "shadow-[0px_6px_15px_0px_#0000000D]": !hasNotScrolled,
          })}
        >
          <SheetTitle className="items-center">
            <img
              src="https://assets.suraasa.com/logos/v2/full-dark.webp"
              alt="logo"
              width={120}
            />
          </SheetTitle>
        </SheetHeader>

        <SheetBody
          className="relative h-full overflow-x-hidden px-2 py-0"
          ref={setSheetBodyRef}
        >
          {/* {steps.map(step => (
            <button
              className="px-2"
              key={step.id}
              onClick={() => setCurrentStep(steps.indexOf(step))}
            >
              {step.id}
            </button>
          ))} */}
          <FormProvider {...form}>
            <Animation
              id={`page-${currentStep}`}
              direction={animationDirection}
              className="h-auto min-h-full"
            >
              <Component
                key={steps[currentStep]?.id}
                deliveryMode={deliveryMode}
                onLoaded={() => setAgreementLoaded(true)}
              />
            </Animation>
          </FormProvider>

          {agreementLoaded && (
            <div className="sticky bottom-0 mt-3 flex justify-end bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.7)_40%,rgba(255,255,255,1)_100%)] p-2 sm:px-[38px] sm:py-3">
              <Button
                onClick={
                  currentStep === steps.length - 1 ? onSubmit : goToNextPage
                }
                className="rounded-[10px]"
                color="black"
                loading={isSubmitting}
              >
                {currentStep === steps.length - 1 ? "Finish" : "Continue"}
              </Button>
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export default PgCTLEnrollmentAgreement
